import { useCallback, useEffect, useState } from "react";
import { BellRing, Download, FileText, Mail, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { formatPhone } from "../lib/phone";
import { Shell } from "../components/Shell";
import { CopyField } from "../components/CopyField";
import { EditDialog } from "../components/EditDialog";
import { UpgradeToCompanyButton } from "../components/UpgradeToCompanyButton";
import { CreditApplicationForm } from "../components/CreditApplicationForm";
import { AccessRequestsPanel } from "../components/AccessRequestsPanel";
import { ShareApplicationButton } from "../components/ShareApplicationButton";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const QUESTIONS = [
  ["phone", "Reference phone number"],
  ["account_duration", "Length of active account"],
  ["terms_extended", "Terms extended"],
  ["highest_balance", "Highest balance granted"],
  ["current_balance", "Current outstanding balance"],
  ["past_due", "Past-due amounts"],
  ["registry_opt_in", "Reference registry opt-in"],
  ["comments", "Additional comments"],
];

export default function CustomerPortal() {
  const { user, refreshUser, setPortal } = useAuth();
  const [me, setMe] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [refs, setRefs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [shareKey, setShareKey] = useState(0);
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "" });

  const load = useCallback(async () => {
    const [m, r] = await Promise.all([api.get("/customer/me"), api.get("/customer/references")]);
    setMe(m.data);
    setRefs(r.data);
    if (m.data.has_logo) {
      try {
        const res = await api.get("/logo", { responseType: "blob" });
        setLogoUrl(URL.createObjectURL(res.data));
      } catch (e) {
        setLogoUrl(null);
      }
    }
  }, []);

  useEffect(() => {
    load().catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const addRef = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/customer/references", {
        ...form,
        origin_url: window.location.origin,
      });
      setForm({ company_name: "", contact_name: "", email: "" });
      toast[data.sent ? "success" : "warning"](
        data.sent
          ? `Reference added — request emailed to ${data.email}`
          : "Reference added, but the email could not be sent. Share the secure link manually."
      );
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/customer/references/${id}`);
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remind = async (r) => {
    try {
      const { data } = await api.post(
        `/customer/references/${r.id}/remind?origin_url=${encodeURIComponent(window.location.origin)}`
      );
      toast[data.sent ? "success" : "warning"](
        data.sent
          ? `Reminder emailed to ${r.email}`
          : "Reminder logged, but the email could not be delivered — share the link manually."
      );
      await load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const emailRequest = async (r) => {
    try {
      const { data } = await api.post(
        `/customer/references/${r.id}/email?origin_url=${encodeURIComponent(window.location.origin)}`
      );
      toast[data.sent ? "success" : "warning"](
        data.sent
          ? `Reference request emailed to ${r.email}`
          : "Email could not be sent right now — share the one-tap link manually."
      );
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const openW9 = async () => {
    try {
      const { data } = await api.get("/w9", { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const origin = window.location.origin;

  return (
    <Shell
      title="Welcome to ConfirmTerms"
      portalLabel="Customer Portal"
      themeName="grey"
      roleLine="Complete your business credit profile and provide trade references to request credit terms."
    >
      <AccessRequestsPanel refreshKey={shareKey} onDecided={load} />

      <div className="ct-rise mb-10 rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        {logoUrl && (
          <img
            src={logoUrl}
            alt={`${me?.vendor_company_name || "Supplier"} logo`}
            data-testid="vendor-logo"
            className="mb-6 h-14 w-auto max-w-[200px] object-contain"
          />
        )}
        <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-blue-600">
          Credit application for {me?.vendor_company_name || "your supplier"}
        </div>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600">
          {me?.vendor_company_name || "Your supplier"} has asked you to complete a credit reference
          check. Add at least <strong className="text-slate-900">two</strong> trade references below —
          company name, contact and email. We email each reference their secure link automatically;
          share the access password with them yourself. Their answers appear here as soon as they are
          submitted.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-slate-100 pt-6">
          <div className="flex items-center gap-2.5 text-sm text-slate-700">
            <FileText className="h-4 w-4 text-blue-600" />
            {me?.has_w9 ? me.w9_filename || "W9.pdf" : "W-9 not yet uploaded by your supplier"}
          </div>
          <ShareApplicationButton onShared={() => setShareKey((n) => n + 1)} />
          {me?.has_w9 && (
            <Button
              variant="outline"
              size="sm"
              onClick={openW9}
              data-testid="customer-view-w9-button"
              className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
            >
              <Download className="mr-1.5 h-3.5 w-3.5" /> View / download W-9
            </Button>
          )}
        </div>

        <div
          className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-md border border-blue-200 bg-blue-50/50 p-5"
          data-testid="upgrade-banner"
        >
          <div>
            <div className="font-display text-base font-bold text-slate-900">
              {user?.can_switch_portal
                ? "Your Vendor/Supplier Portal is ready"
                : "Do you extend credit to your own customers?"}
            </div>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              {user?.can_switch_portal
                ? "Switch to “My customers” at the top of the page to set up customers and collect their trade references."
                : "Start your own ConfirmTerms workspace on this same login and begin collecting trade references from your customers."}
            </p>
          </div>
          {user?.can_switch_portal ? (
            <Button
              size="sm"
              onClick={() => setPortal("company")}
              data-testid="go-to-company-portal"
              className="rounded-full bg-blue-600 px-5 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95"
            >
              Open my Vendor/Supplier Portal
            </Button>
          ) : (
            <UpgradeToCompanyButton
              selfServe
              testId="self-upgrade"
              label="Start my own workspace"
              customerId={me?.id}
              customerName={me?.company_name}
              onDone={async () => {
                await refreshUser();
                setPortal("company");
              }}
            />
          )}
        </div>
      </div>

      <div className="ct-rise mb-10">
        <CreditApplicationForm />
      </div>

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <form
          onSubmit={addRef}
          data-testid="reference-form"
          className="h-fit rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
        >
          <h2 className="font-display text-xl font-extrabold tracking-tight text-slate-900">
            Add a credit reference
          </h2>
          <p className="mt-1.5 text-sm text-slate-500">
            {refs.length < 2
              ? `${2 - refs.length} more required (3 recommended).`
              : "Add more if needed."}
          </p>
          <div className="mt-6 space-y-5">
            {[
              ["company_name", "Reference company name", "text"],
              ["contact_name", "Contact name", "text"],
              ["email", "Contact email", "email"],
            ].map(([key, label, type]) => (
              <div key={key}>
                <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {label}
                </Label>
                <Input
                  type={type}
                  required
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  data-testid={`ref-${key}-input`}
                  className="mt-2 h-11 border-slate-300 focus-visible:ring-blue-600"
                />
              </div>
            ))}
            <Button
              type="submit"
              disabled={busy}
              data-testid="add-reference-button"
              className="h-11 w-full rounded-md bg-blue-600 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-[0.99]"
            >
              <Plus className="mr-2 h-4 w-4" /> {busy ? "Adding…" : "Add reference"}
            </Button>
          </div>
        </form>

        <div className="space-y-4" data-testid="references-list">
          {refs.length === 0 ? (
            <div
              className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center"
              data-testid="references-empty"
            >
              <p className="font-display text-lg font-bold text-slate-900">No references added</p>
              <p className="mt-2 text-sm text-slate-500">
                Add your first trade reference to generate their secure link.
              </p>
            </div>
          ) : (
            refs.map((r) => (
              <div
                key={r.id}
                className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
                data-testid={`my-reference-${r.id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-lg font-bold tracking-tight text-slate-900">
                      {r.company_name}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600">
                      {r.contact_name} · {r.email}
                    </p>
                    {r.reminder_count > 0 && (
                      <p
                        className="mt-1.5 text-xs font-semibold text-amber-700"
                        data-testid={`reminder-info-${r.id}`}
                      >
                        {r.reminder_count} reminder{r.reminder_count === 1 ? "" : "s"} sent · last{" "}
                        {new Date(r.last_reminded_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${
                        r.status === "completed"
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                      data-testid={`reference-status-${r.id}`}
                    >
                      {r.status === "completed" ? "Completed" : "Awaiting response"}
                    </span>
                    <EditDialog
                      testId={`edit-my-reference-${r.id}`}
                      title="Edit reference profile"
                      endpoint={`/references/${r.id}`}
                      onSaved={load}
                      fields={[
                        ["company_name", "Reference company name"],
                        ["contact_name", "Contact name"],
                        ["email", "Contact email", "email"],
                      ]}
                      initial={{
                        company_name: r.company_name,
                        contact_name: r.contact_name,
                        email: r.email,
                      }}
                    />
                    {r.status !== "completed" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => emailRequest(r)}
                        data-testid={`email-reference-${r.id}`}
                        className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
                      >
                        <Mail className="mr-1.5 h-3.5 w-3.5" /> Resend request
                      </Button>
                    )}
                    {r.status !== "completed" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => remind(r)}
                        data-testid={`remind-reference-${r.id}`}
                        className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
                      >
                        <BellRing className="mr-1.5 h-3.5 w-3.5" /> Send reminder
                      </Button>
                    )}
                    {r.status !== "completed" && (
                      <button
                        onClick={() => remove(r.id)}
                        data-testid={`delete-reference-${r.id}`}
                        className="rounded p-1.5 text-slate-400 transition-colors duration-200 hover:bg-red-50 hover:text-red-600"
                        aria-label="Remove reference"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="mt-5 grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2">
                  <CopyField
                    label="One-tap link (no code needed)"
                    value={`${origin}${r.magic_link || r.link}`}
                    testId={`reference-link-${r.id}`}
                  />
                  <CopyField
                    label="Access code (backup)"
                    value={r.access_password}
                    testId={`reference-password-${r.id}`}
                  />
                </div>

                {r.responses && (
                  <dl className="mt-5 grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2">
                    {QUESTIONS.filter(([k]) => r.responses[k]).map(([k, label]) => (
                      <div key={k}>
                        <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                          {label}
                        </dt>
                        <dd className="mt-1 text-sm text-slate-900">
                          {k === "phone" ? formatPhone(r.responses[k]) : r.responses[k]}
                        </dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </Shell>
  );
}
